import type { MembershipStatus } from "../types";

interface StatusBadgeProps {
  status: MembershipStatus;
  /** Override variant — bypasses MembershipStatus logic for non-membership use cases. */
  variant?: "green" | "pink";
  /** Override label text — use when displaying non-membership statuses (e.g. "Refunded"). */
  label?: string;
}

/**
 * @component StatusBadge
 * @description Badge indicating a status (membership, booking, etc.).
 * Uses design system typography and color utilities.
 * @param status - Current membership status (ACTIVE or EXPIRED). Ignored when `variant` is set.
 * @param variant - Explicit color variant; overrides status-based color logic.
 * @param label - Explicit label text; overrides the status value.
 * @returns A styled `<span>` element with the status text.
 */
export default function StatusBadge({ status, variant, label }: StatusBadgeProps) {
  const isPink = variant === "pink" || (!variant && status !== "ACTIVE");

  return (
    <span
      className={`px-2.5 py-1.5 text-label-extra uppercase tracking-wider leading-none border shrink-0 ${
        isPink
          ? "text-accent-pink border-accent-pink"
          : "text-charcoal border-accent-green"
      }`}
      aria-label={label ?? `Membership status: ${status}`}
    >
      {label ?? status}
    </span>
  );
}
